import { useCallback, useEffect, useRef, useState } from "react";
import type { StudioController } from "../useStudio";
import { LAUNCHPAD_CHANGED, LAUNCHPAD_STORAGE, matchesShortcut, readLaunchpad, saveLaunchpad, type LaunchpadConfig, type LaunchpadKey } from "./config";

export function useLaunchpad(studio: StudioController) {
  const [config, setConfig] = useState(readLaunchpad);
  const latest = useRef(studio);
  latest.current = studio;
  useEffect(() => {
    const refresh = () => setConfig(readLaunchpad());
    const storage = (event: StorageEvent) => { if (event.key === LAUNCHPAD_STORAGE || event.key === null) refresh(); };
    window.addEventListener(LAUNCHPAD_CHANGED, refresh);
    window.addEventListener("storage", storage);
    return () => { window.removeEventListener(LAUNCHPAD_CHANGED, refresh); window.removeEventListener("storage", storage); };
  }, []);
  const activate = useCallback((key: LaunchpadKey) => {
    const { state, command, parsed, notify } = latest.current;
    if (key.action === "blackout") command({ type: "blackout" });
    else if (key.action === "clear") command({ type: "cue", cue: null });
    else if (key.action === "toggle") command({ type: "toggle" });
    else if (key.action === "reset") command({ type: "reset" });
    else if (key.action === "restart-section") {
      const markers = parsed.markers.filter(marker => marker.wordIndex <= state.wordIndex);
      const blocks = parsed.blocks.filter(block => block.wordStart <= state.wordIndex);
      const wordIndex = markers.at(-1)?.wordIndex ?? blocks.at(-1)?.wordStart ?? 0;
      command({ type: "pause" });
      command({ type: "cue", cue: null });
      command({ type: "seek", wordIndex });
    } else {
      const current = state.visualCue;
      if (current?.id === key.id && (!current.expiresAt || current.expiresAt > Date.now())) command({ type: "cue", cue: null });
      else {
        command({ type: "cue", pause: key.pause, cue: { id: key.id, text: key.text, picture: key.picture, color: key.color, layout: key.layout, expiresAt: key.durationSeconds ? Date.now() + key.durationSeconds * 1000 : null } });
        if (state.blackout) notify("Cue is ready behind Blackout. Restore output to show it.");
      }
    }
  }, []);
  useEffect(() => {
    if (!config.shortcutsEnabled) return;
    const keydown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.isComposing || document.querySelector("dialog[open]")) return;
      const target = event.target;
      if (target instanceof Element && target.closest('input,textarea,select,[contenteditable="true"],[role="textbox"],[data-production-notes]')) return;
      const key = config.keys.slice(0, config.count).find(key => matchesShortcut(event, key.shortcut) || matchesShortcut(event, key.numpad));
      if (!key) return;
      event.preventDefault();
      event.stopPropagation();
      if (!event.repeat) activate(key);
    };
    document.addEventListener("keydown", keydown, true);
    return () => document.removeEventListener("keydown", keydown, true);
  }, [config, activate]);
  const expiry = studio.state.visualCue?.expiresAt;
  const cueId = studio.state.visualCue?.id;
  useEffect(() => {
    if (!expiry) return;
    const expire = () => {
      const current = latest.current.state.visualCue;
      if (current && current.id === cueId && current.expiresAt === expiry && Date.now() >= expiry) latest.current.command({ type: "cue", cue: null });
    };
    const timer = window.setTimeout(expire, Math.max(0, Math.min(2147483647, expiry - Date.now())));
    window.addEventListener("focus", expire);
    document.addEventListener("visibilitychange", expire);
    return () => { clearTimeout(timer); window.removeEventListener("focus", expire); document.removeEventListener("visibilitychange", expire); };
  }, [expiry, cueId]);
  const save = (draft: LaunchpadConfig) => { setConfig(saveLaunchpad(draft)); };
  return { config, activate, save };
}
